import { useEffect, useReducer, useContext } from "react";
import { sendRequest } from "@/common/http";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import translate from "@/i18n/translate";
import ReportingYears, {
  dateWithoutTimezone
} from "../common/elements/ReportingYears";
import { WmButton } from "@watermarkinsights/ripple-react";
import { formatDate } from "../common/helper";
import WidgetLoader from "../common/elements/WidgetLoader";
import { getLoaderStatus } from "../common/helper";
import ApiError from "../common/elements/ApiError";
import {
  ReportingYear,
  MembershipInterface
} from "../common/interfaces/Interface";
import { AccomplishmentsChart } from "./AccomplishmentsChart";
const { REDIRECT_URI } = runConfig;

interface Accomplishment {
  category: string;
  count: number;
  order: number;
}

interface FacultySuccessState {
  showScholarshipSummaryWidget: boolean;
  selectedReportingYear: ReportingYear;
  accomplishments: Accomplishment[];
  mappingId: string | null;
  totalAccomplishments: number | null | undefined;
  loader: boolean;
  summaryError: boolean;
}
interface ScholarshipSummary {
  category: string;
  count: number;
  order: number;
}

interface scholarshipSummaryResult {
  scholarshipSummary: ScholarshipSummary[];
  showFsTab: boolean;
}
interface InstitutionMapping {
  mappingId: string;
  showFsTab: boolean;
}

interface HeaderProp {
  selectReportingYear: (year: ReportingYear) => void;
  reportingYears: ReportingYear[] | null;
  selectedReportingYear: ReportingYear;
  ryApiError: boolean;
}
interface FacultySuccessProps {
  reportingYears: ReportingYear[] | null;
  defaultReportingYear: ReportingYear;
  ryApiError: boolean;
  organizationView: boolean;
}

interface FacultySuccessAction {
  type: string;
  year: ReportingYear;
  institutionMapping: InstitutionMapping;
  scholarshipSummary: scholarshipSummaryResult;
}

const setScholarshipSummary = (
  state: FacultySuccessState,
  scholarshipSummary: scholarshipSummaryResult
) => {
  if (scholarshipSummary && scholarshipSummary.scholarshipSummary) {
    const total = scholarshipSummary.scholarshipSummary.find(
      (acc: Accomplishment) => acc.category === "Total"
    );
    const accomplishments: Array<Accomplishment> =
      scholarshipSummary.scholarshipSummary
        .filter((acc: Accomplishment) => acc.category !== "Total")
        .sort((acc1: Accomplishment, acc2: Accomplishment) => {
          return acc1.order > acc2.order ? 1 : -1;
        });

    return {
      ...state,
      accomplishments: accomplishments,
      totalAccomplishments: total && total.count,
      loader: false
    };
  } else {
    return {
      ...state,
      loader: false,
      summaryError: true
    };
  }
};

const FacultySuccess = ({
  reportingYears,
  defaultReportingYear,
  ryApiError,
  organizationView
}: FacultySuccessProps) => {
  const { membership, institution } =
    useContext<AppContextInterface>(AppContext);
  const initialState = {
    showScholarshipSummaryWidget: false,
    selectedReportingYear: defaultReportingYear,
    accomplishments: [],
    mappingId: null,
    totalAccomplishments: null,
    loader: true,
    summaryError: false
  };

  const defaultFacultySuccessAction = {
    type: "",
    year: defaultReportingYear,
    institutionMapping: {
      mappingId: "",
      showFsTab: false
    },
    scholarshipSummary: {
      scholarshipSummary: [],
      showFsTab: false
    }
  };

  const stateReducer = (
    state: FacultySuccessState,
    action: FacultySuccessAction
  ) => {
    switch (action.type) {
      case "showLoader":
        return { ...state, loader: true, summaryError: false };
      case "setScholarshipSummary":
        return setScholarshipSummary(state, action.scholarshipSummary);

      case "selectReportingYear":
        return {
          ...state,
          selectedReportingYear: action.year
        };
      case "setMappingId":
        return {
          ...state,
          mappingId: action.institutionMapping.mappingId,
          showScholarshipSummaryWidget: action.institutionMapping.showFsTab
        };
      case "setSummaryError":
        return { ...state, loader: false, summaryError: true };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(stateReducer, initialState);

  const getURL = () => {
    const queryString = window.location.search;
    const param = new URLSearchParams(queryString);
    const locale = param.get("locale");
    const isPlanning = window.location.pathname.includes("/planning/");
    const base = isPlanning ? `/insights/planning` : `/insights`;
    let url = `${base}/institution/${institution?.platformUuid ?? ""}/faculty_success/reporting_year/${state.selectedReportingYear.uuid}/mapping_id/${state.mappingId}/html`;
    if (locale !== null) {
      url = `${url}?locale=${locale}`;
    }

    return url;
  };

  const openFacultySuccessHTMLViewLink = () => {
    return window.open(getURL(), "_blank");
  };

  useEffect(() => {
    if (institution && institution.salesforceId !== "") {
      const applyResult = (result: InstitutionMapping) => {
        dispatch({
          ...defaultFacultySuccessAction,
          type: "setMappingId",
          institutionMapping: result
        });
      };

      sendRequest(
        {
          uri: `/faculty_success/institution_mapping`,
          camelize: true
        },
        applyResult
      );
    }
  }, [institution && institution.salesforceId]);

  useEffect(() => {
    if (
      state.mappingId &&
      state.selectedReportingYear &&
      state.selectedReportingYear.startDate
    ) {
      const applyResult = (result: scholarshipSummaryResult) => {
        dispatch({
          ...defaultFacultySuccessAction,
          type: "setScholarshipSummary",
          scholarshipSummary: result
        });
      };
      const onError = () => {
        dispatch({
          ...defaultFacultySuccessAction,
          type: "setSummaryError"
        });
      };
      sendRequest(
        {
          uri:
            "/faculty_success/scholarship_summary" +
            "?mapping_id=" +
            state.mappingId +
            "&start_date=" +
            dateWithoutTimezone(state.selectedReportingYear?.startDate) +
            "&end_date=" +
            dateWithoutTimezone(state.selectedReportingYear?.endDate),
          camelize: true
        },
        applyResult,
        onError
      );

      dispatch({ ...defaultFacultySuccessAction, type: "showLoader" });
    }
  }, [state.mappingId, state.selectedReportingYear]);

  useEffect(() => {
    const liveRegion = document.querySelector(".faculty-loader-status");
    getLoaderStatus(state.loader, liveRegion);
  }, [state.loader]);

  const selectReportingYear = (year: ReportingYear) => {
    dispatch({
      ...defaultFacultySuccessAction,
      type: "selectReportingYear",
      year: year
    });
  };

  useEffect(() => {
    selectReportingYear(defaultReportingYear);
  }, [defaultReportingYear]);

  const ScholarshipSummary = () => {
    return (
      <div className="wm-grid-row">
        <span
          className="sr-only faculty-loader-status"
          role="status"
          aria-live="assertive"
        ></span>
        <div className="grid-container wm-grid-col-12 wm-grid-col-md-10">
          <div className="paper-card paper-card-height">
            <div className="header">
              <div className="info">
                <h3 className="title">
                  {translate("app.faculty.widgetLabel")}
                </h3>
                <p className="description">
                  {translate("app.faculty.widgetSummary")}
                </p>
              </div>
              {state.totalAccomplishments && state.totalAccomplishments > 0 ? (
                <div>
                  <WmButton
                    id="export-button"
                    tabIndex={0}
                    onClick={openFacultySuccessHTMLViewLink}
                    button-type="secondary"
                  >
                    {translate("app.exportHtml")}
                  </WmButton>
                </div>
              ) : (
                ""
              )}
            </div>
            {state.loader && (
              <WidgetLoader
                loaderContainerClasses="loader-container"
                loaderClasses="widget-loader"
              />
            )}
            {!state.loader && !state.summaryError && (
              <div className="body fs-body">
                {organizationView && (
                  <div className="results-filtered-by">
                    <strong>
                      {translate("app.home.showingResultsForAllOrganizations")}
                    </strong> {institution?.name}
                  </div>
                )}
                {state.totalAccomplishments &&
                  state.totalAccomplishments > 0 ? (
                  <div className="wm-grid">
                    <Accomplishments />
                  </div>
                ) : (
                  <NoAccomplishments />
                )}
              </div>
            )}
            {state.summaryError && <ApiError />}
          </div>
        </div>
      </div>
    );
  };

  const Accomplishments = () => {
    return (
      <div className="flex-row accomplishments-container">
        <div className="">
          <div className="accomplishments-heading">
            {translate("app.faculty.totalAccomplishments")}
          </div>

          <div className="accomplishments-count">
            {state.totalAccomplishments}
          </div>
          {state.selectedReportingYear &&
            state.selectedReportingYear.startDate && (
              <div className="accomplishments-years">
                {translate("app.faculty.accomplishmentsYear", {
                  startDate: `${formatDate(
                    state.selectedReportingYear?.startDate
                  )}`,
                  endDate: `${formatDate(state.selectedReportingYear?.endDate)}`
                })}
              </div>
            )}
        </div>
        <div className="chart" id="scholarship-summary">
          <AccomplishmentsChart
            accomplishments={state.accomplishments}
            forHtml={false}
          />
        </div>
      </div>
    );
  };

  const showScholarshipSummary = () => {
    return reportingYears && reportingYears.length > 0 ? (
      <ScholarshipSummary />
    ) : (
      membership && reportingYearEmptyState(membership)
    );
  };

  return (
    <>
      {state.showScholarshipSummaryWidget && (
        <section
          className="body wm-grid tab-body-home"
          id="sse-widget"
        >
          <section className="insights">
            <Header
              selectReportingYear={selectReportingYear}
              reportingYears={reportingYears}
              selectedReportingYear={state.selectedReportingYear}
              ryApiError={ryApiError}
            />
            {ryApiError && <ApiError />}
            {!ryApiError && reportingYears !== null ? (
              <>{showScholarshipSummary()}</>
            ) : (
              ""
            )}
          </section>
        </section>
      )}
    </>
  );
};

const Header = (props: HeaderProp) => {
  return (
    <div className="institution-name -bottom flex-row -justified sse-desc">
      <h3 className="helper-label helper-default-margin">
        {translate("app.faculty.facultyInsightsLabel")}
      </h3>

      {!props.ryApiError &&
        props.reportingYears &&
        props.reportingYears.length > 0 &&
        props.selectedReportingYear.uuid && (
          <ReportingYears
            reportingYears={props.reportingYears}
            selectedReportingYear={props.selectedReportingYear}
            selectReportingYear={props.selectReportingYear}
          />
        )}
    </div>
  );
};

const NoAccomplishments = () => {
  return (
    <div className="no-plans">
      <div className="no-plans-text">
        {translate("app.faculty.noAccomplishments")}
      </div>
      <div className="no-plans-text-select">
        {translate("app.faculty.noAccomplishmentsSubMsg")}
      </div>
    </div>
  );
};

const reportingYearEmptyState = (membership: MembershipInterface) => {
  return (
    <div className="academic-yr-empty-state flex-row">
      <div>
        <div className="empty-state-title">
          {translate("app.continuousImprovement.noAcademicYearTitle")}
        </div>
        {membership.userRole === "admin" && adminEmptyState()}
        {membership.userRole === "contributor" && contributorEmptyState()}
      </div>
    </div>
  );
};

function contributorEmptyState() {
  return (
    <div className="empty-state-text">
      {translate("app.faculty.contactAdmin")}
    </div>
  );
}

function adminEmptyState() {
  return (
    <div className="empty-state-text">
      <a
        className="empty-state-link"
        href={`${REDIRECT_URI}/admin/configuration_settings/reporting_year`}
      >
        {translate("app.faculty.createReportingYear")}
      </a>
      {translate("app.faculty.ryEmptyMessage")}
    </div>
  );
}

export default FacultySuccess;
